import React, { useState } from 'react';

const MAX_POSTS = 6;

function Field({ id, label, value, onChange, multiline, hint }) {
  return (
    <div className="col-12">
      <label className="form-label" htmlFor={id}>
        {label}
      </label>
      {multiline ? (
        <textarea
          id={id}
          className="form-control"
          rows={typeof multiline === 'number' ? multiline : 3}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <input id={id} className="form-control" value={value} onChange={(e) => onChange(e.target.value)} />
      )}
      {hint ? <div className="form-text">{hint}</div> : null}
    </div>
  );
}

function PostEditor({ index, post, onChange, onRemove, canRemove }) {
  const setPost = (patch) => onChange({ ...post, ...patch });

  const setSection = (sectionIndex, patch) => {
    const sections = [...(post.sections || [])];
    sections[sectionIndex] = { ...sections[sectionIndex], ...patch };
    setPost({ sections });
  };

  const addSection = () => {
    setPost({ sections: [...(post.sections || []), { heading: '', body: '' }] });
  };

  const removeSection = (sectionIndex) => {
    setPost({ sections: (post.sections || []).filter((_, i) => i !== sectionIndex) });
  };

  return (
    <div className="row g-3">
      <div className="col-12 d-flex justify-content-between align-items-center">
        <span className="small fw-semibold text-muted">Article {index + 1}</span>
        {canRemove ? (
          <button type="button" className="btn btn-link btn-sm text-danger p-0" onClick={onRemove}>
            Remove article
          </button>
        ) : null}
      </div>
      <div className="col-md-6">
        <Field
          id={`blog-post-${index}-title`}
          label="Title"
          value={post.title || ''}
          onChange={(v) => setPost({ title: v })}
        />
      </div>
      <div className="col-md-6">
        <Field
          id={`blog-post-${index}-slug`}
          label="URL slug"
          value={post.slug || ''}
          onChange={(v) => setPost({ slug: v })}
          hint="Used in /blog/your-slug — lowercase letters, numbers, and hyphens only."
        />
      </div>
      <Field
        id={`blog-post-${index}-excerpt`}
        label="Short summary (homepage card + article intro)"
        value={post.excerpt || ''}
        onChange={(v) => setPost({ excerpt: v })}
        multiline={3}
      />
      <div className="col-md-4">
        <Field
          id={`blog-post-${index}-date`}
          label="Date (optional)"
          value={post.date || ''}
          onChange={(v) => setPost({ date: v })}
          hint="YYYY-MM-DD"
        />
      </div>
      {(post.sections || []).map((sec, i) => (
        <div key={`post-${index}-sec-${i}`} className="col-12 border rounded p-3 bg-light">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="small fw-semibold">Section {i + 1}</span>
            <button type="button" className="btn btn-link btn-sm text-danger p-0" onClick={() => removeSection(i)}>
              Remove
            </button>
          </div>
          <Field
            id={`blog-post-${index}-h-${i}`}
            label="Section heading"
            value={sec.heading || ''}
            onChange={(v) => setSection(i, { heading: v })}
          />
          <Field
            id={`blog-post-${index}-b-${i}`}
            label="Section body"
            value={sec.body || ''}
            onChange={(v) => setSection(i, { body: v })}
            multiline={4}
          />
        </div>
      ))}
      <div className="col-12">
        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={addSection}>
          Add section
        </button>
      </div>
    </div>
  );
}

export default function AdminSiteBlogFields({ form, setField }) {
  const blog = form.blog_content || { section_title: '', section_subtitle: '', posts: [] };
  const posts = Array.isArray(blog.posts) ? blog.posts : [];
  const [active, setActive] = useState(0);

  const setBlog = (patch) => {
    setField('blog_content', { ...blog, ...patch });
  };

  const setPost = (index, post) => {
    const next = [...posts];
    next[index] = post;
    setBlog({ posts: next });
  };

  const addPost = () => {
    if (posts.length >= MAX_POSTS) return;
    const stamp = Date.now();
    setBlog({
      posts: [
        ...posts,
        {
          slug: `article-${stamp}`,
          title: 'New article',
          excerpt: '',
          date: new Date().toISOString().slice(0, 10),
          sections: [{ heading: '', body: '' }],
        },
      ],
    });
    setActive(posts.length);
  };

  const removePost = (index) => {
    if (posts.length <= 1) return;
    const next = posts.filter((_, i) => i !== index);
    setBlog({ posts: next });
    setActive(Math.max(0, Math.min(active, next.length - 1)));
  };

  const safeActive = Math.min(active, Math.max(0, posts.length - 1));

  return (
    <>
      <div className="card border shadow-sm mb-3">
        <div className="card-header py-2">
          <h2 className="h6 mb-0">Gift & styling ideas</h2>
          <p className="small text-muted mb-0 mt-1">
            Homepage cards and full articles at <code>/blog</code>. Up to {MAX_POSTS} articles.
          </p>
        </div>
        <div className="card-body row g-3">
          <Field
            id="blog-section-title"
            label="Section heading (homepage)"
            value={blog.section_title || ''}
            onChange={(v) => setBlog({ section_title: v })}
          />
          <Field
            id="blog-section-subtitle"
            label="Section subtitle (homepage + blog index)"
            value={blog.section_subtitle || ''}
            onChange={(v) => setBlog({ section_subtitle: v })}
            multiline={2}
          />
        </div>
      </div>

      <div className="card border shadow-sm mb-3">
        <div className="card-header py-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h2 className="h6 mb-0">Articles</h2>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={addPost}
            disabled={posts.length >= MAX_POSTS}
          >
            Add article
          </button>
        </div>
        <div className="card-body">
          {posts.length === 0 ? (
            <p className="text-muted small mb-0">No articles yet. Add one or save to restore defaults.</p>
          ) : (
            <>
              <div className="admin-site-section-nav mb-3" role="tablist">
                {posts.map((p, i) => (
                  <button
                    key={`${p.slug}-${i}`}
                    type="button"
                    role="tab"
                    className={`admin-site-section-nav__tab${safeActive === i ? ' admin-site-section-nav__tab--active' : ''}`}
                    onClick={() => setActive(i)}
                  >
                    {p.title?.trim() || `Article ${i + 1}`}
                  </button>
                ))}
              </div>
              <PostEditor
                index={safeActive}
                post={posts[safeActive]}
                onChange={(post) => setPost(safeActive, post)}
                onRemove={() => removePost(safeActive)}
                canRemove={posts.length > 1}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
}
